import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';

import { generateWorkflowPath } from '~/core/application/routing/generateWorkflowPath';
import { RootDocument } from '../../../../../../types';

/**
 * Redirects the user to the root document's current place when they land on a
 * workflow URL whose `:place` segment differs from where the document actually
 * is (e.g. a hand-typed URL jumping ahead of the prescribed flow).
 *
 * The check runs once per session mount, on the first successful root document
 * load; every subsequent place change is driven by
 * `useNavigateToPlaceInWorkflow` and must not be re-checked here. The redirect
 * replaces the history entry so the invalid place is not reachable via back.
 */
export const useRedirectToWorkflowCurrentPlace = (
  rootDocument: RootDocument | undefined,
  workflowName: string,
  place: string | undefined,
  currentPlace: string | undefined,
): void => {
  const navigate = useNavigate();
  const hasCheckedLandingPlace = useRef(false);

  useEffect(() => {
    if (hasCheckedLandingPlace.current) return;
    if (!rootDocument || !currentPlace) return;

    hasCheckedLandingPlace.current = true;
    if (place && place !== currentPlace) {
      navigate(
        generateWorkflowPath(rootDocument.shortId, workflowName, currentPlace),
        { replace: true },
      );
    }
  }, [rootDocument, workflowName, place, currentPlace, navigate]);
};
